import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { offlineRadio, similarRadios } from '@/mocks/radioOffline';

function formatLastLive(iso: string): string {
  const d = new Date(iso);
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const dia = d.getDate();
  const mes = meses[d.getMonth()];
  const anio = d.getFullYear();
  const h = d.getHours().toString().padStart(2, '0');
  const min = d.getMinutes().toString().padStart(2, '0');
  return `${dia} ${mes} ${anio}, ${h}:${min}`;
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.floor((now - then) / 1000);

  if (diff < 60) return 'hace menos de un minuto';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `hace ${m} ${m === 1 ? 'minuto' : 'minutos'}`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `hace ${h} ${h === 1 ? 'hora' : 'horas'}`;
  }
  const d = Math.floor(diff / 86400);
  return `hace ${d} ${d === 1 ? 'día' : 'días'}`;
}

export default function RadioOfflinePage() {
  const [following, setFollowing] = useState(false);
  const radio = offlineRadio;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-16 md:pt-18">
        {/* Hero / Offline Banner */}
        <section className="relative w-full bg-gradient-to-b from-foreground-950 via-foreground-950 to-foreground-900 overflow-hidden">
          {/* Background subtle pattern */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(circle at 25px 25px, white 1px, transparent 0)',
              backgroundSize: '50px 50px',
            }}
          ></div>

          <div className="relative max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
            {/* Logo */}
            <div className="w-24 h-24 md:w-28 md:h-28 mx-auto mb-6 rounded-2xl border-2 border-foreground-700/50 overflow-hidden bg-background-50/5 backdrop-blur-sm relative">
              <img
                src={radio.logoUrl}
                alt={radio.name}
                className="w-full h-full object-cover"
              />
              {/* Pulse ring for offline effect */}
              <div className="absolute inset-0 rounded-2xl border-2 border-amber-500/30"></div>
            </div>

            {/* Frequency badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-800/70 border border-foreground-700/50 mb-3">
              <i className="ri-broadcast-line text-amber-400 text-xs"></i>
              <span className="text-amber-300 font-bold text-sm font-mono tracking-wider">{radio.frequency}</span>
              <span className="text-foreground-400 text-[10px]">RFW</span>
            </div>

            {/* Name */}
            <h1 className="text-foreground-50 font-bold text-2xl md:text-3xl mb-3">{radio.name}</h1>

            {/* Location */}
            <p className="text-foreground-400 text-xs mb-2">
              <i className="ri-map-pin-line text-[10px] mr-1"></i>
              {radio.city}, {radio.state}
            </p>

            {/* Description */}
            <p className="text-foreground-500 text-sm max-w-lg mx-auto leading-relaxed">
              {radio.description}
            </p>
          </div>
        </section>

        {/* Offline Status Section */}
        <section className="relative z-10 -mt-8 px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 shadow-sm">
              {/* Status icon & message */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 md:w-18 md:h-18 rounded-full bg-amber-50 flex items-center justify-center mb-4 relative">
                  <i className="ri-signal-wifi-off-line text-amber-500 text-2xl md:text-3xl"></i>
                  {/* Animated rings */}
                  <span className="absolute inset-0 rounded-full border-2 border-amber-300/40 animate-ripple"></span>
                  <span className="absolute inset-0 rounded-full border border-amber-300/30 animate-ripple-delay-1"></span>
                </div>

                <h2 className="text-foreground-950 font-bold text-lg md:text-xl mb-1.5">
                  Esta radio está temporalmente fuera de línea
                </h2>
                <p className="text-foreground-500 text-sm max-w-md leading-relaxed">
                  {radio.offlineReason}
                </p>
              </div>

              {/* Last live time */}
              <div className="flex items-center justify-center gap-2 mb-6 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 mx-auto max-w-fit">
                <i className="ri-history-line text-foreground-400 text-sm"></i>
                <span className="text-foreground-600 text-xs">
                  Última vez en vivo: <strong className="text-foreground-800">{formatLastLive(radio.lastLiveAt)}</strong>
                </span>
                <span className="text-foreground-400 text-[10px]">({timeAgo(radio.lastLiveAt)})</span>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => setFollowing(!following)}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    following
                      ? 'bg-accent-100 text-accent-700 border border-accent-200'
                      : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 active:scale-[0.98]'
                  }`}
                >
                  <i className={`${following ? 'ri-user-follow-fill' : 'ri-user-follow-line'} text-base`}></i>
                  {following ? 'Siguiendo' : 'Seguir'}
                  {!following && (
                    <span className="text-background-50/60 font-normal text-[10px]">
                      {radio.followers.toLocaleString()}
                    </span>
                  )}
                </button>

                <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold border border-background-300 text-foreground-700 hover:bg-background-100 hover:border-foreground-300 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-image-line text-base"></i>
                  Ver fotos
                  <span className="text-foreground-400 font-normal text-[10px]">{radio.totalPhotos}</span>
                </button>

                <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold border border-background-300 text-foreground-700 hover:bg-background-100 hover:border-foreground-300 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-calendar-event-line text-base"></i>
                  Ver eventos
                  <span className="text-foreground-400 font-normal text-[10px]">{radio.totalEvents}</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* RFW Radio Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200/60 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                <i className="ri-radio-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-2">La plataforma nunca se queda en silencio</h3>
              <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6">
                Mientras esta radio está fuera del aire, escuchá <strong>RFW Radio</strong>, la estación oficial de la plataforma. Siempre disponible, siempre en vivo.
              </p>
              <Link
                to="/radio/87-00"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl transition-all cursor-pointer text-sm shadow-lg shadow-amber-500/20 whitespace-nowrap"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                Escuchar RFW Radio
              </Link>
              <p className="text-foreground-400 text-[10px] mt-4">
                RFW Radio · 87.00 RFW · Contenido oficial · Siempre disponible
              </p>
            </div>
          </div>
        </section>

        {/* Similar Radios Section */}
        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-widest mb-2">
                Mientras tanto
              </p>
              <h3 className="text-foreground-950 font-bold text-xl md:text-2xl">
                Radios similares que están al aire
              </h3>
              <p className="text-foreground-500 text-sm mt-2 max-w-lg mx-auto">
                Descubrí otras estaciones con contenido parecido. Todas transmitiendo ahora mismo.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {similarRadios.map((radio) => (
                <Link
                  key={radio.id}
                  to={`/radio/${radio.frequency.replace('.', '-')}`}
                  className="group bg-background-50 border border-background-200 rounded-2xl p-5 flex items-center gap-4 hover:border-background-300 transition-all cursor-pointer"
                >
                  {/* Logo */}
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden shrink-0 bg-background-100 relative">
                    <img
                      src={radio.logoUrl}
                      alt={radio.name}
                      className="w-full h-full object-cover"
                    />
                    {radio.isLive && (
                      <div className="absolute top-0 right-0 w-4 h-4 bg-emerald-500 rounded-full border-2 border-background-50 translate-x-1 -translate-y-1"></div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-foreground-950 font-semibold text-sm truncate group-hover:text-foreground-900 transition-colors">
                        {radio.name}
                      </h4>
                    </div>
                    <p className="text-foreground-400 text-[11px] mb-1">{radio.category}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-foreground-400 text-[10px] font-mono font-semibold">{radio.frequency}</span>
                      <span className="text-foreground-300 text-[10px]">·</span>
                      <span className="flex items-center gap-1 text-foreground-400 text-[10px]">
                        <i className="ri-user-line text-[9px]"></i>
                        {radio.listeners.toLocaleString()}
                      </span>
                      {radio.isLive && (
                        <>
                          <span className="text-foreground-300 text-[10px]">·</span>
                          <span className="flex items-center gap-1 text-emerald-500 text-[10px] font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            En vivo
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
                  </div>
                </Link>
              ))}
            </div>

            {/* Explore more link */}
            <div className="text-center mt-8">
              <Link
                to="/explorar"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-background-300 text-foreground-600 text-sm font-medium hover:bg-background-100 hover:border-foreground-300 transition-all cursor-pointer"
              >
                <i className="ri-compass-3-line text-base"></i>
                Explorar más radios
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}